import { ReactElement } from "react"
import { LocalizeContextProps } from "react-localize-redux"
import { IPatient, IInsurance } from "../../../constants/types"
import { InsuranceClaim as InsuranceClaimType } from "../InsuranceClaims/types"

export interface Bill{
    uuid?:string,
    id:number,
    documentNumber:number,
    uuidPatient:string,
    billItems:BillItem[],
    type:DocumentType,
    status:DocumentStatus,
    uuidDepartment:string | null,
    uuidPrescribingDoctor:string | null,
    totalGross:number,
    totalDiscount:number,
    totalPaid:number,
    createdAt:Date,
    updatedAt:Date
}

export interface Billable{
    id:number,
    concept:string,
    amount:number,
    insurance?:string,
    type: number,
    quantity?:number,
}

export interface BillableCombo{
    id:number,
    name:string,
    billables:{id:number}[]
}

export interface BillItem{
    id?:number
    billableId?:number,
    concept:string,
    type:number,
    quantity:number,
    unitCost:number,
    amountAlter?:number,
    paymentMethodInvestigationId?:number,
    paymentData?:{
        bankName?:string,
        chequeNumber?:string
    },
    additionalInfoId?:number,
    relatedBillables?:number[],
    amount:number | string,
    updatedAt?:Date,
    createdAt?:Date,
    used?: boolean,
    paid?: boolean,
    order?: number
}

export interface PaymentMethod{
    id:number,
    name:string,
    code:string,
    customName:string,
    paymentData?:{
        bankName?:string,
        chequeNumber?:string
    },
}

export interface BillItemTable{
    id:number,
    concept:ReactElement,
    type?:ReactElement,
    colSpan?:BillItemKeys,
    amount:ReactElement,
    delete : JSX.Element,
    used?: JSX.Element,
    paid?: JSX.Element
}

export enum BillItemKeys{
    concept = "concept",
    amount = "amount",
    type = "type",
    quantity = "quantity",
}

export interface BillingInfo{
    id:number,
    address:string,
    currency:string,
    phone:string,
    email:string,
    city:string,
    logoBlob:string,
    billables:Billable[],
    hospitalName:string,
    params:BillingInfoParams,
}

export interface BillingInfoParams {
    permissionDiscount?:boolean,
    discounts?:boolean,
    insurances?:boolean,
    budgets?:boolean,
}

export enum DocumentType{
    SUMMARY = 0,
    BUDGET = 1,
    INVOICE = 2,
}

export enum DocumentStatus{
    DRAFT = 0,
    CLOSED = 1,
}

export interface EditBillingInfoProps {
    callbackUpdate : (values:any) => void
    billingInfo:BillingInfo,
}

export interface EditBillablesProps {
    billables : Billable[],
    billingInfo:BillingInfo,
    uuidInvestigation : string,
    withDiscount : boolean,
    onBillablesCreated:(billItems:BillItem[]) =>void
}

export interface EditBillingProps extends LocalizeContextProps, Omit<EditBillablesProps, | 'onBillablesCreated' >, Omit<EditBillingInfoProps, 'callbackUpdate'> {
    onBillingInfoSuccesfullyUpdated : (type:BillItemModes) => void;
}

export interface BillingInfoServiceResponse {
    status:number, billingInfo:BillingInfo
}

export interface BillablesServiceResponse {
    status: number, billables: Billable[] 
}

export interface BillServiceResponse {
    status: number, bill:Bill
}

// New interfaces for updated bill endpoint
export interface PaymentRecord {
    id: number,
    amount: number,
    paymentMethodInvestigationId: number,
    createdByResearcherUuid?: string,
    paymentData?: {
        note?: string,
        reference?: string,
        bankName?: string,
        chequeNumber?: string
    } | null,
    paidAt: string,
    createdAt: string
}

export interface BillUpdateResponse {
    status: number,
    bill: Bill & {
        paymentRecords: PaymentRecord[]
    },
    paymentRecord: PaymentRecord | null
}

export interface BillUpdateErrorResponse {
    status: number,
    errorMessage: string,
    errorCode: number
}

// Type for creating/sending payment data (subset of PaymentRecord)
export type CreatePaymentRequest = Pick<PaymentRecord, 'amount' | 'paymentMethodInvestigationId' | 'paymentData'>

export enum BillItemModes{
    SHOW = "SHOW",
    EDIT = "EDIT",
    // BILL = "bill",
    // BILLABLE = "billable",
    // GENERAL = "general"
}

// Insurance Bills Report Types
export interface Organization {
    id: number;
    name: string;
    uuid?: string;
}

export interface InsuranceBillsReportData {
    organization: Organization;
    bills: BillWithPEC[];
    startDate?: Date;
    endDate?: Date;
    totalApprovedAmount: number;
}

export interface BillWithPEC {
    bill: Bill;
    patient: IPatient;
    pec: InsuranceClaimType;
    approvedAmount: number;
}

// Insurance Bills Types
export interface InsuranceBill {
    uuid: string;
    organizationUuid: string;
    organizationName: string;
    totalGross: number;
    totalPaid: number;
    totalDiscount: number;
    status: DocumentStatus; // 0 = DRAFT, 1 = CLOSED
    type: DocumentType; // 2 = INVOICE
    billItems?: BillItem[];
    paymentRecords?: PaymentRecord[];
    createdAt: string;
    updatedAt: string;
}

export interface InsuranceBillsResponse {
    status: number;
    bills: InsuranceBill[];
    errorMessage?: string;
}

export interface InsuranceBillResponse {
    status: number;
    bill?: InsuranceBill;
    errorMessage?: string;
}

// Insurance Claim for Insurance Bills (simplified version from API)
export interface InsuranceBillClaim {
    uuid: string;
    requestedAmount: number;
    approvedAmount?: number;
    coveragePercentage: number;
    status: string; // 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'PARTIALLY_APPROVED' | 'DENIED' | 'PAID'
    guaranteeLetterNumber: string;
    guaranteeLetterIssueDate: string;
    interventionDate: string;
    organization: {
        id: number;
        name: string;
        uuid: string;
    };
    bill: {
        uuid: string;
        billIdInvestigation: number;
    };
    createdAt: string;
    updatedAt: string;
}

export interface ClaimsResponse {
    status: number;
    insuranceClaims?: InsuranceBillClaim[];
    errorMessage?: string;
}
